import {Injectable} from '@angular/core';
import {Tile, TileId, TileIdMap, TileFolderDisplayMode, TILE_DEFAULT_WRITE_PRIORITY, TILE_TYPE_SUPER_CANVAS, TILE_TYPE_SUPER_FOLDER} from '../../shared/models/tile';
import {HttpClient, HttpParams} from '@angular/common/http';
import {SessionService} from './session.service';
import {AppState} from '../../app/app.state';
import {
  TileLinkedToAppsAction,
  TileLoaded,
  TilesForAppLoadedAction,
  TilesForUserLoadedAction,
  TilesLoadedAction,
  TilesTileDeletedAction,
  TilesTileSavedAction,
  TileUnlinkedFromAppsAction,
} from '../actions/tiles.actions';
import {Store} from '@ngrx/store';
import {BehaviorSubject, forkJoin, Observable, ObservableInput, of, ReplaySubject, throwError} from 'rxjs';
import {TileValue} from '../../shared/models/tile-value';
import {SimpleStructure, SuperFolderStructure} from '../../shared/models/structure';
import {API_BASE_URL, TUNNEL_BASE_URL} from './env.service';
import {BacnetPriorityArray} from '../../shared/models/bacnet';
import {ShadeToolsService} from './shade-tools.service';
import {map, switchMap, take} from 'rxjs/operators';
import {AppId} from '../../shared/models/app';
import {PermissionsService} from './permissions.service';
import {Permission} from 'src/shared/models/fga/permission';
import {ObjectType} from 'src/shared/models/object.model';
import {Tag} from '../../shared/models/tag';

const TILE_BATCH_MAX_IDS = 500;

@Injectable({
  providedIn: 'root',
})
export class TilesService {
  private fetchedUserTilesOnce: boolean = false;
  private tileRequests = new Map<TileId, ReplaySubject<Tile>>();
  private queuedTileIds = new Set<TileId>();
  private pendingTilesTimer: ReturnType<typeof setTimeout> | null = null;

  sessUserTiles: TileIdMap = {};
  sessUserTiles$: BehaviorSubject<TileIdMap> = new BehaviorSubject<TileIdMap>({});
  tileValues$: BehaviorSubject<{ [tileId: TileId]: TileValue }>;
  tileValues: { [tileId: TileId]: TileValue } = {};

  serverTimeOffset: number;



  constructor(
    private session: SessionService,
    private http: HttpClient,
    private store: Store<AppState>,
    private shadeToolsService: ShadeToolsService,
    private permissionsService: PermissionsService,
  ) {
    this.tileValues$ = new BehaviorSubject<{ [p: TileId]: TileValue }>({});


    this.store.select('tiles').subscribe(
      (state) => {
        if (state.all) {
          for (let tileId in state.all) {
            this.sessUserTiles[tileId] = state.all[tileId];
          }

          this.sessUserTiles$.next(this.sessUserTiles);
        }
      },
    );
  }


  canConfigureTile$(tileId: TileId): Observable<boolean> {
    return this.permissionsService.hasPermissions({
      UserID: this.session.userId,
      Permissions: [Permission.CAN_CONFIGURE],
      ObjectID: tileId,
      ObjectType: ObjectType.TILE,
    });
  }

  /**
   *
   * @param tileId
   */
  fetchTile$(tileId: TileId): Observable<Tile> {
    return this.http.get<Tile>(`${API_BASE_URL}/tiles/${tileId}`).pipe(
      map(tile => {
        const created = Tile.create(tile);
        this.store.dispatch(new TileLoaded(created));
        return created;
      })
    );
  }



  fetchTileBatched$(tileId: TileId): Observable<Tile> {
    return new Observable<Tile>(subscriber => {
      let request = this.tileRequests.get(tileId);
      if (!request) {
        request = new ReplaySubject<Tile>(1);
        this.tileRequests.set(tileId, request);
        this.queuedTileIds.add(tileId);
        this.pendingTilesTimer ??= setTimeout(() => this.flushPendingTiles(), 0);
      }
      return request.pipe(take(1)).subscribe(subscriber);
    });
  }

  private flushPendingTiles(): void {
    this.pendingTilesTimer = null;

    const queued = [...this.queuedTileIds];
    this.queuedTileIds.clear();

    const ids = queued.filter(id => {
      if (this.tileRequests.get(id)?.observed) return true;
      this.tileRequests.delete(id);
      return false;
    });

    for (let i = 0; i < ids.length; i += TILE_BATCH_MAX_IDS) {
      this.fetchTileBatch(ids.slice(i, i + TILE_BATCH_MAX_IDS));
    }
  }

  private fetchTileBatch(ids: TileId[]): void {
    const request$ = ids.length === 1
      ? this.http.get<Tile>(`${API_BASE_URL}/tiles/${ids[0]}`).pipe(map(tile => [tile]))
      : this.http.get<Tile[]>(`${API_BASE_URL}/tiles`, {params: {TileIDs: ids.join(',')}});

    request$.subscribe({
      next: tiles => {
        let created: Tile[];
        try {
          created = tiles.map(tile => Tile.create(tile));
          this.store.dispatch(new TilesLoadedAction(created.map(tile => Tile.create(tile))));
        } catch (err) {
          this.failTileRequests(ids, err);
          return;
        }

        const byId = new Map(created.map(tile => [tile.ID, tile]));
        ids.forEach(id => {
          const request = this.tileRequests.get(id)!;
          this.tileRequests.delete(id);
          const tile = byId.get(id);
          if (tile) {
            request.next(tile);
            request.complete();
          } else {
            request.error(new Error(`Tile ${id} was not returned`));
          }
        });
      },
      error: err => this.failTileRequests(ids, err),
    });
  }

  private failTileRequests(ids: TileId[], err: unknown): void {
    ids.forEach(id => {
      const request = this.tileRequests.get(id)!;
      this.tileRequests.delete(id);
      request.error(err);
    });
  }

  /**
   *
   * @param parentTileIds
   */
  fetchTilesAndDescendants$(parentTileIds: TileId[]): Observable<Tile[]> {
    return this.http.get<Tile[]>(
      `${API_BASE_URL}/tiles`,
      {
        params: {
          TileIDs: parentTileIds.join(','),
          IncludeChildren: 'true',
        },
      },
    ).pipe(
      map(tiles => {
        const tilesForStore = tiles.map(t => Tile.create(t));
        this.store.dispatch(new TilesLoadedAction(tilesForStore));
        return tiles.map(t => Tile.create(t));
      })
    );
  }


  /**
   * Returns an observable that emits the user's tiles. More critically,
   * if user tiles have already been fetched from server, returns an
   * observable that emits that cached array; otherwise triggers a fetch
   * from the API and returns observable that emits the response from
   * the server.
   */
  getUserTiles$(): Observable<Tile[]> {
    if (this.fetchedUserTilesOnce) {
      return of(Object.values(this.sessUserTiles));
    }

    return this.fetchUserTiles$();
  }


  /**
   *
   */
  fetchUserTiles$(): Observable<Tile[]> {
    const userId = this.session.userId;

    return this.http.get<Tile[]>(`${API_BASE_URL}/tiles/user/${userId}`).pipe(
      map(tiles => {
        const tilesForStore = tiles.map(t => Tile.create(t));
        this.store.dispatch(new TilesForUserLoadedAction(tilesForStore));
        this.fetchedUserTilesOnce = true;
        return tilesForStore;
      })
    );
  }


  /**
   *
   * @param appId
   */
  fetchAppTiles$(appId: AppId): Observable<Tile[]> {
    return this.http.get<Tile[]>(`${API_BASE_URL}/tiles/app/${appId}`).pipe(
      map(rawAppTiles => {
        const tilesForStore = rawAppTiles.map(t => Tile.create(t));
        this.store.dispatch(new TilesForAppLoadedAction(tilesForStore, appId));
        return tilesForStore;
      })
    );
  }

  /**
   *
   * @param tile
   * @return Observable<TileId> Turns from 0 to new tile ID when successful
   */
  createTile$(tile: Tile): Observable<TileId> {
    return this.http.post<Tile>(`${API_BASE_URL}/tiles`, tile).pipe(
      map(newTile => {
        const createdTile = Tile.create(newTile);
        this.store.dispatch(new TilesTileSavedAction(createdTile));

        return createdTile.ID;
      })
    );
  }


  /**
   * Save tile data (name, structure, appearance, etc.)
   *
   * IMPORTANT: This ONLY updates the tile document, NOT FGA parent-child relationships.
   *
   * Why are these separate?
   * - Tile Structure (ordering): Cosmetic setting that lives in the tile document
   * - FGA relationships: Authorization/permission inheritance that lives in FGA system
   *
   * The backend intentionally does NOT auto-sync these because:
   * 1. They answer different questions (display order vs. permissions)
   * 2. Keeping two sources of truth in sync adds complexity
   * 3. Each system should be the single source of truth for its concern
   *
   * When updating child tiles, you must call BOTH:
   * 1. saveTile$() to update Structure.TileIDs or Structure.Points
   * 2. replaceChildrenOfTile$() to update FGA parent-child relationships
   *
   * @param tile Tile to save
   * @return Observable<null> Returns null if/when call succeeds
   */
  saveTile$(tile: Tile): Observable<null> {
    return this.http.put(`${API_BASE_URL}/tiles/${tile.ID}`, tile).pipe(
      map(() => {
        this.store.dispatch(new TilesTileSavedAction(Tile.create(tile)));
        return null;
      })
    );
  }


  updateTileTags(tileId: TileId, tags: Tag[]): void {
    const tile = this.sessUserTiles[tileId];
    if (!tile) return;
    const updatedTile = Tile.create(tile);
    updatedTile.Tags = tags;
    this.store.dispatch(new TilesTileSavedAction(updatedTile));
  }


  /**
   *
   * @param tileId number
   */
  deleteTile$(tileId: TileId): Observable<null> {
    return this.http.delete(`${API_BASE_URL}/tiles/${tileId}`).pipe(
      map(() => {
        this.store.dispatch(new TilesTileDeletedAction(tileId));

        return null;
      })
    );
  }


  /**
   *
   * @param tileValue
   */
  sendTileValue$(tileValue: TileValue): BehaviorSubject<boolean> {
    this.tileValues[tileValue.TileID] = tileValue;

    const confirm = new BehaviorSubject<boolean>(false);
    this.http.put<TileValue>(`${TUNNEL_BASE_URL}/remote/tile-values`, tileValue).subscribe({
      next: () => {
        confirm.next(true);
        confirm.complete();
      },
      error: () => {
        this.shadeToolsService.errorDialog('The value could not be changed. Try again.');
        confirm.next(false);
        confirm.error(null);
      }
    });

    return confirm;
  }


  /**
   * This returns an array of BacnetPriorityArray in anticipation of someday
   * supporting more than one point per tile.
   *
   * @param tileId
   */
  fetchPriorityArrays$(tileId: TileId): Observable<BacnetPriorityArray[]> {
    return this.http.get<BacnetPriorityArray[]>(`${TUNNEL_BASE_URL}/remote/bacnet/point-priority-arrays`, {
      params: {
        TileID: '' + tileId,
      },
    });
  }


  /**
   *
   * @param priorityArrays
   */
  sendPriorityArrays$(priorityArrays: BacnetPriorityArray[]): Observable<BacnetPriorityArray[]> {
    return this.http.put<BacnetPriorityArray[]>(`${TUNNEL_BASE_URL}/remote/bacnet/point-priority-arrays`, priorityArrays);
  }


  /**
   * A wrapper around linkWithApp$ and unlinkFromApp$ when both need to be called
   * at the same time.
   *
   * @param tileId
   * @param linkedAppIds
   * @param unlinkedAppIds
   * @return {Observable<boolean>} - Emits true if a call actually is made; false
   *    if no call was necessary
   * @see linkWithApp$
   * @see unlinkFromApp$
   */
  updateAppLinks$(tileId: TileId, linkedAppIds: AppId[], unlinkedAppIds: AppId[]): Observable<boolean> {
    const sources: ObservableInput<any>[] = [];

    if (linkedAppIds.length) {
      sources.push(this.linkWithApp$(tileId, linkedAppIds));
    }

    if (unlinkedAppIds.length) {
      sources.push(this.unlinkFromApp$(tileId, unlinkedAppIds));
    }

    if (sources.length) {
      return forkJoin(sources).pipe(
        map(() => true)
      );
    } else {
      return of(false);
    }
  }



  /**
   *
   * @param tileId
   * @param appIds
   * @return {Observable<boolean>} - Turns true if/when call succeeds
   */
  linkWithApp$(tileId: TileId, appIds: AppId[]): Observable<boolean> {
    return this.http.post(`${API_BASE_URL}/tiles/${tileId}/app/${appIds.join(',')}`, null).pipe(
      map(() => {
        this.store.dispatch(new TileLinkedToAppsAction(tileId, appIds));
        return true;
      })
    );
  }


  /**
   *
   * @param tileId
   * @param appIds
   * @return {Observable<boolean>} - Turns true if/when call succeeds
   */
  unlinkFromApp$(tileId: TileId, appIds: AppId[]): Observable<boolean> {
    return this.http.delete(`${API_BASE_URL}/tiles/${tileId}/app/${appIds.join(',')}`).pipe(
      map(() => {
        this.store.dispatch(new TileUnlinkedFromAppsAction(tileId, appIds));
        return true;
      })
    );
  }

  /**
   * Replace entire set of FGA child relationships for a tile (folder or canvas)
   *
   * IMPORTANT: This ONLY updates FGA relationships, NOT Structure.TileIDs or Structure.Points.
   *
   * Why are these separate?
   * - Tile Structure (ordering): Cosmetic setting - "What is the order of tiles displayed?"
   * - FGA relationships: Authorization - "What are the parent/child relationships for permissions?"
   *
   * The backend intentionally does NOT auto-sync these because:
   * 1. They answer different questions (display order vs. permission inheritance)
   * 2. Keeping two sources of truth in sync adds complexity
   * 3. Each system should be the single source of truth for its concern
   *
   * When updating child tiles, you must call BOTH:
   * 1. saveTile$() to update Structure.TileIDs or Structure.Points
   * 2. replaceChildrenOfTile$() to update FGA parent-child relationships
   *
   * @param tileId Parent tile ID
   * @param childIds Array of child tile IDs (replaces entire set)
   * @return {Observable<null>} - Returns null if/when call succeeds
   */
  replaceChildrenOfTile$(tileId: TileId, childIds: TileId[]): Observable<null> {
    const url = `${API_BASE_URL}/tiles/${tileId}/children`
    const body = { "ChildIDs": childIds }

    return this.http.put(url, body).pipe(
      map(() => {
        return null;
      })
    );
  }


  addChildrenToFolder$(folderId: TileId, childIds: TileId[]): Observable<TileId[]> {
    if (childIds.includes(folderId)) {
      return throwError(() => new Error('A folder cannot be added to itself'));
    }

    return this.fetchTilesAndDescendants$([folderId]).pipe(
      switchMap(tiles => {
        const folder = tiles.find(t => t.ID === folderId);

        if (!folder) {
          return throwError(() => new Error(`Folder ${folderId} not found`));
        }

        if (folder.Type !== TILE_TYPE_SUPER_FOLDER) {
          return throwError(() => new Error(`Tile ${folderId} is not a folder`));
        }

        const existingChildIds = tiles.filter(t => t.ID !== folderId).map(t => t.ID);
        const newChildIds = [...new Set(childIds)].filter(id => !existingChildIds.includes(id));

        if (newChildIds.length === 0) {
          return of(existingChildIds);
        }

        const allChildIds = [...existingChildIds, ...newChildIds];
        const structure = folder.Structure as SuperFolderStructure;
        const orderedIds = structure?.TileIDs ?? [];
        const structureChildIds = [...orderedIds, ...allChildIds.filter(id => !orderedIds.includes(id))];

        return this.replaceChildrenOfTile$(folderId, allChildIds).pipe(
          switchMap(() => {
            if (structure) {
              structure.TileIDs = structureChildIds;
            } else {
              folder.Structure = {
                DisplayMode: TileFolderDisplayMode.Default,
                Priority: TILE_DEFAULT_WRITE_PRIORITY,
                TileIDs: structureChildIds,
              } as SuperFolderStructure;
            }

            return this.saveTile$(folder);
          }),
          map(() => allChildIds),
        );
      }),
    );
  }


  /**
   * Fetch tile values from the API
   * @param tileIds
   */
  fetchTileValues$(tileIds: TileId[]): Observable<TileValue[]> {
    let params = new HttpParams();
    params = params.append('TileIDs', JSON.stringify(tileIds));

    return this.http.get<TileValue[]>(`${TUNNEL_BASE_URL}/remote/tile-values`, {
      params: params,
      observe: 'response',
    }).pipe(
      map(resp => {
        for (let val of resp.body) {
          if (val.Value !== null) {
            this.tileValues[val.TileID] = val;
          }
        }

        const cloudTime = resp.headers.get('x-motion-cloud-time');

        if (cloudTime) {
          this.serverTimeOffset = new Date(+cloudTime).getTime() - Date.now();
        }

        this.tileValues$.next(this.tileValues);

        return resp.body;
      })
    );
  }


}
